/**
 * AddToListMenu - Dropdown menu for adding cards to lists
 *
 * Shows a list of user's lists with checkmarks for lists that already
 * contain the card. Supports adding with quantity and creating new lists inline.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoCheckmark, IoList } from 'react-icons/io5';
import { PiListChecks, PiListChecksFill } from 'react-icons/pi';

import useCardIndex from '@/hooks/useCardIndex';
import { calculateDropdownPosition } from '@/hooks/useDropdownPosition';

import { useAddCardToList, useCardListEntries, useCreateList, useLists, useRemoveCardFromList } from '../hooks/api';

interface AddToListMenuProps {
  oracleId: string;
  cardName: string;
  /** Specific Scryfall edition ID. When provided, enables edition-aware dedup. */
  scryfallId?: string;
  /** Button variant - icon only or with text */
  variant?: 'icon' | 'button';
  /** Custom class for the trigger button */
  className?: string;
  /** Mount with dropdown already open (used by lazy-mount pattern) */
  initialOpen?: boolean;
  /** Dropdown horizontal alignment relative to trigger */
  dropdownAlign?: 'left' | 'right';
}

export default function AddToListMenu({
  oracleId,
  cardName,
  scryfallId,
  variant = 'icon',
  className,
  initialOpen = false,
  dropdownAlign = 'right',
}: AddToListMenuProps): ReactElement {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [addQuantity, setAddQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');
  const [expandedListId, setExpandedListId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect vertical space when dropdown opens
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const pos = calculateDropdownPosition(menuRef.current, {
        placement: 'bottom-start',
        estimatedHeight: 350,
        estimatedWidth: 256,
      });
      setOpenDirection(pos.placement === 'bottom' ? 'down' : 'up');
    }
  }, [isOpen]);

  // Data hooks
  const { data: lists } = useLists();
  const cardEntries = useCardListEntries(oracleId);
  const addCard = useAddCardToList();
  const removeCard = useRemoveCardFromList();
  const createListFn = useCreateList();
  const { getCardById } = useCardIndex();

  // Map of list_id -> true for lists containing this card
  const listsWithCard = new Set(cardEntries.map((e) => e.list_id));
  const isOnAnyList = listsWithCard.size > 0;

  // Group entries by list_id for edition-aware display
  const entriesByList = useMemo(() => {
    const map = new Map<string, typeof cardEntries>();
    for (const entry of cardEntries) {
      const existing = map.get(entry.list_id) ?? [];
      existing.push(entry);
      map.set(entry.list_id, existing);
    }
    return map;
  }, [cardEntries]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowNewList(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setShowNewList(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Focus input when showing new list form
  useEffect(() => {
    if (showNewList && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showNewList]);

  const handleToggleList = async (listId: string, isInList: boolean) => {
    setIsLoading(true);
    try {
      if (isInList) {
        const entries = entriesByList.get(listId) ?? [];

        if (scryfallId) {
          // Edition-aware: check if there's an exact scryfall_id match
          const exactEntry = entries.find((e) => e.scryfall_id === scryfallId);
          if (exactEntry) {
            // Remove the exact edition entry
            await removeCard(exactEntry.id);
          } else {
            // Different edition(s) exist — expand to show editions
            setExpandedListId(expandedListId === listId ? null : listId);
            setIsLoading(false);
            return;
          }
        } else {
          // No scryfallId — legacy toggle: remove first entry
          const entry = entries[0];
          if (entry) {
            await removeCard(entry.id);
          }
        }
      } else {
        await addCard({
          list_id: listId,
          oracle_id: oracleId,
          name: cardName,
          scryfall_id: scryfallId,
          quantity: addQuantity,
          priority: 'medium',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEdition = async (listId: string) => {
    setIsLoading(true);
    try {
      await addCard({
        list_id: listId,
        oracle_id: oracleId,
        name: cardName,
        scryfall_id: scryfallId,
        quantity: addQuantity,
        priority: 'medium',
      });
      setExpandedListId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveEdition = async (entryId: string) => {
    setIsLoading(true);
    try {
      await removeCard(entryId);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newListName.trim()) return;

    setIsLoading(true);
    try {
      const newListId = await createListFn({
        name: newListName.trim(),
      });

      // Add card to the new list
      await addCard({
        list_id: newListId,
        oracle_id: oracleId,
        name: cardName,
        scryfall_id: scryfallId,
        quantity: addQuantity,
        priority: 'medium',
      });

      setNewListName('');
      setShowNewList(false);
    } catch (error) {
      console.error('Failed to create list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateAndAdd();
    }
  };

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger button */}
      {variant === 'icon' ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx('rounded-lg p-1.5 transition-colors', isOnAnyList ? 'text-teal' : 'text-ash hover:text-teal', className)}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={isOnAnyList ? 'On list - click to manage' : 'Add to list'}
          title={isOnAnyList ? 'On list' : 'Add to list'}
        >
          {isOnAnyList ? <PiListChecksFill className="h-5 w-5" /> : <PiListChecks className="h-5 w-5" />}
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors',
            isOnAnyList ? 'text-teal hover:bg-teal/10' : 'text-silver hover:text-pearl hover:bg-slate',
            className,
          )}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {isOnAnyList ? <PiListChecksFill className="h-4 w-4" /> : <PiListChecks className="h-4 w-4" />}
          List
        </button>
      )}

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={clsx(
            'border-storm bg-obsidian shadow-void/50 absolute z-50 w-64 rounded-lg border py-1 shadow-xl',
            openDirection === 'down' ? 'top-full mt-2' : 'bottom-full mb-2',
            dropdownAlign === 'left' ? 'left-0' : 'right-0',
          )}
        >
          {/* Header */}
          <div className="border-storm flex items-center justify-between border-b px-3 py-2">
            <span className="text-silver text-xs font-medium tracking-wide uppercase">Add to List</span>
            {/* Quantity selector */}
            <div className="flex items-center gap-1">
              <span className="text-ash text-xs">Qty:</span>
              <input
                type="number"
                min={1}
                max={99}
                value={addQuantity}
                onChange={(e) => setAddQuantity(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                className="bg-slate border-storm text-pearl w-12 rounded border px-1.5 py-0.5 text-center text-xs"
              />
            </div>
          </div>

          {/* Lists */}
          <div className="max-h-64 overflow-y-auto py-1">
            {lists.length === 0 ? (
              <p className="text-ash px-4 py-3 text-center text-sm">No lists yet</p>
            ) : (
              lists.map((list) => {
                const isInList = listsWithCard.has(list.id);
                const entries = entriesByList.get(list.id) ?? [];
                const totalQty = entries.reduce((sum, e) => sum + e.quantity, 0);
                const hasExactEdition = scryfallId ? entries.some((e) => e.scryfall_id === scryfallId) : false;
                const hasDifferentEditions = scryfallId ? isInList && !hasExactEdition : false;
                const isExpanded = expandedListId === list.id;

                return (
                  <div key={list.id}>
                    <button
                      onClick={() => handleToggleList(list.id, isInList)}
                      disabled={isLoading}
                      className={clsx(
                        'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                        isInList ? 'text-teal hover:bg-teal/10' : 'text-silver hover:bg-slate hover:text-pearl',
                      )}
                    >
                      <span
                        className={clsx(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs',
                          isInList ? 'bg-teal/20' : 'bg-slate',
                        )}
                      >
                        {isInList ? <IoCheckmark className="h-3.5 w-3.5" /> : <IoList className="text-ash h-3.5 w-3.5" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{list.name}</span>
                      {totalQty > 0 && <span className="text-ash text-xs">×{totalQty}</span>}
                    </button>

                    {/* Edition sub-entries when different editions exist on this list */}
                    {isExpanded && hasDifferentEditions && (
                      <div className="bg-storm/30 border-storm/50 mx-2 mb-1 rounded border">
                        {entries.map((entry) => {
                          const editionCard = getCardById(entry.scryfall_id);
                          const setLabel = editionCard
                            ? `${editionCard.set.toUpperCase()} #${editionCard.collector_number}`
                            : entry.scryfall_id.slice(0, 8);
                          return (
                            <div key={entry.id} className="text-ash flex items-center gap-2 px-3 py-1.5 text-xs">
                              <span className="min-w-0 flex-1 truncate">{setLabel}</span>
                              <span>×{entry.quantity}</span>
                              <button
                                onClick={() => handleRemoveEdition(entry.id)}
                                disabled={isLoading}
                                className="text-ash transition-colors hover:text-red-400"
                                aria-label={`Remove ${setLabel} from list`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                        {/* Add current edition option */}
                        {scryfallId &&
                          (() => {
                            const currentCard = getCardById(scryfallId);
                            const currentLabel = currentCard
                              ? `${currentCard.set.toUpperCase()} #${currentCard.collector_number}`
                              : 'this edition';
                            return (
                              <button
                                onClick={() => handleAddEdition(list.id)}
                                disabled={isLoading}
                                className="text-aether hover:bg-aether/10 flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors"
                              >
                                <IoAdd className="h-3 w-3" />
                                <span>Add as {currentLabel}</span>
                              </button>
                            );
                          })()}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Create new list */}
          <div className="border-storm border-t pt-1">
            {showNewList ? (
              <div className="px-3 py-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="List name..."
                  className="bg-slate border-storm text-pearl placeholder:text-ash focus:border-aether mb-2 w-full rounded-lg border px-3 py-1.5 text-sm focus:outline-none"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowNewList(false);
                      setNewListName('');
                    }}
                    className="text-ash hover:text-silver px-2 py-1 text-xs transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAndAdd}
                    disabled={!newListName.trim() || isLoading}
                    className="bg-aether hover:bg-aether/80 text-pearl rounded px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Creating...' : 'Create & Add'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewList(true)}
                className="text-aether hover:bg-aether/10 flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors"
              >
                <IoAdd className="h-4 w-4" />
                Create new list
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
